import React, { useState, useEffect, useRef } from 'react';
import { Stage, Layer, Rect, Image as KonvaImage, Text, Line, Arrow, Group, Circle } from 'react-konva';
import { useTranslation } from 'react-i18next';
import tireImageSrc from '../../../images/pneumatico.png'; // Import tire image
import texture2Image from '../../../images/graniglia-pav.png';
import texture4Image from '../../../images/subgrade.png';

// se te lo stai chiedendo questo è x UNPAVED !!!

// Interfacce per le Props (come prima)
export interface ILayerData {
    name: string;
    thickness: number;
    color: string;
}

export interface IGeogridInfo {
    positionFromBottomOfBase: number; // Dal fondo del Base Course
    label?: string;
    color?: string;
}

/**
 * Props per TireAndLayers: rappresenta la sezione trasversale con tre strati (surfaceLayer, baseCourse, subgrade) e le linee opzionali di geogrid/geotextile.
 * - surfaceLayer è ora opzionale per compatibilità con Results_Paved.tsx.
 * - geogrid e geotextile sono sempre supportati tramite le rispettive props.
 */
interface Props {
    surfaceLayer?: ILayerData;
    baseCourse: ILayerData;
    subgrade: ILayerData; // Spessore = altezza visibile
    leftDimensionValue?: number;
    rightDimensionValue?: number;
    savingPercentage?: number;
    geogrid?: IGeogridInfo;
    geotextileLabel?: string; // Dynamic label for geotextile
    subbaseGeogrid?: IGeogridInfo;
    unitLabel?: string;
    showLeftDimension?: boolean;
    showRightDimension?: boolean;
    showSavingBox?: boolean;
    showGeogrid?: boolean;
    showTire?: boolean;
    showLayers?: boolean;
    showGeotextile?: boolean;
    leftDimensionStrokeWidth?: number;
    horizontalSpacing?: number;
    verticalSpacing?: number;
    customStageHeight?: number;
    onStageHeightCalculated?: (height: number) => void;
    onSubgradeYCalculated?: (y: number) => void;
    alignFromBottom?: boolean;
    targetSubgradeY?: number;
    hideLayerTitles?: boolean;
    projectType?: 'container_yard' | 'parking_deck' | string;
    forcedStageWidth?: number;
    rectWidth?: number; // Larghezza dei rettangoli dei layer, opzionale per personalizzazione
    rectStartXOffset?: number;
    stageType?: 'geogrid' | 'geotextile' | 'unstabilized' | string;
    unstabilizedStageHeight?: number;
}

// --- Componente ---
const TireAndLayers: React.FC<Props> = ({
    baseCourse,
    subgrade,
    leftDimensionValue,
    rightDimensionValue,
    savingPercentage,
    geogrid,
    geotextileLabel = 'Geotextile', // Default fallback
    unitLabel = 'm',
    showLeftDimension = false,
    showRightDimension = true,
    showSavingBox = false,
    showGeogrid = false,
    showGeotextile = false,
    showTire = true,
    showLayers = true,
    customStageHeight,
    onStageHeightCalculated,
    onSubgradeYCalculated,
    alignFromBottom = false,
    targetSubgradeY,
    hideLayerTitles = false,
    forcedStageWidth,
    rectWidth = 360, // Larghezza dei rettangoli dei layer, di default 200
    rectStartXOffset = 0, // Offset per la posizione X dei rettangoli
    unstabilizedStageHeight,
}) => {
    const { t } = useTranslation();
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [texture2Img, setTexture2Img] = useState<HTMLImageElement | null>(null);
    const [texture4Img, setTexture4Img] = useState<HTMLImageElement | null>(null);
    // --- Calcoli Dimensioni e Posizioni (2 STRATI) ---
    const scale = 150;
    const baseCourseHeightPx = Math.max(baseCourse.thickness * scale, 1);
    const subgradeHeightPx = Math.max(subgrade.thickness * scale, 1);
    const stageTopMargin = 10;
    const stageBottomMargin = 10;
    const totalLayerHeight = baseCourseHeightPx + subgradeHeightPx;
    const titleAreaWidth = 68;
    const leftDimAreaWidth = 50;
    //const rectStartX = titleAreaWidth; // 40px per il margine sinistro
    const squareWidth = rectWidth; // Larghezza rettangoli livelli
    const rectStartX = titleAreaWidth + rectStartXOffset;
    const rectEndX = rectStartX + squareWidth;
    const rightDimAreaWidth_aprx = 80;
    const savingBoxWidth = 160;
    const savingBoxHeight = 70;
    const stageWidth = rectWidth + titleAreaWidth + rightDimAreaWidth_aprx;
    const TIRE_IMAGE_HEIGHT = 150;
    const TIRE_RAPPORTO_DIAGONALE = 2.2;
    const TIRE_IMAGE_WIDTH = TIRE_IMAGE_HEIGHT / TIRE_RAPPORTO_DIAGONALE;
    const currentImageHeight = TIRE_IMAGE_HEIGHT;
    const currentImageWidth = TIRE_IMAGE_WIDTH;
    const tireImageY = stageTopMargin;
    let baseY: number, subgradeY: number, stageHeight: number;

    // === QUOTE (DIMENSIONI) ===
    const dimColor = '#004652';
    const marrone = '#9e480e';
    const dimStrokeWidth = 2;
    const thickDimLineWidth = 22;
    const thickDimLineSpacing = 16;
    const extensionLineOffset = 0;
    const extensionLineLength = 30;
    const dimensionLineOffsetX = 16;
    const textOffsetY = -6;
    const dimTextProps = { fontSize: 12, fill: 'black', fontStyle: 'bold' };
    const dimArrowProps = { stroke: marrone, fill: marrone, strokeWidth: 1, pointerLength: 9, pointerWidth: 8 };
    const leftDimLineX = rectStartX - extensionLineOffset - dimensionLineOffsetX;
    const rightDimLineX = rectEndX + extensionLineOffset + dimensionLineOffsetX;
    const savingLabelOffsetY = 0;
    const savingBoxY = stageTopMargin + Math.abs(savingLabelOffsetY);
    const geogridColor = geogrid?.color || '#24244f';
    const layerStrokeColor = '#444444';
    const layerStrokeWidth = 0.5;
    // Standardized label measurement for geogrid/geotextile
    const geogridLabelPadding = 20;
    // Ref e stato per larghezza/altezza testo geogrid
    const geogridTextRef = useRef<any>(null);
    const [geogridTextWidth, setGeogridTextWidth] = useState(0);
    const [geogridTextHeight, setGeogridTextHeight] = useState(0);
    // Ref e stato per geotextile
    const geotextileTextRef = useRef<any>(null);
    const [geotextileTextWidth, setGeotextileTextWidth] = useState(0);
    const [geotextileTextHeight, setGeotextileTextHeight] = useState(0);

    useEffect(() => {
        const imgToLoad = new window.Image();
        imgToLoad.src = tireImageSrc;
        imgToLoad.onload = () => setImage(imgToLoad);
        imgToLoad.onerror = () => {};
        const tex2 = new window.Image();
        tex2.src = texture2Image;
        tex2.onload = () => setTexture2Img(tex2);
        tex2.onerror = () => {};
        const tex4 = new window.Image();
        tex4.src = texture4Image;
        tex4.onload = () => setTexture4Img(tex4);
        tex4.onerror = () => {};
    }, []);
    // Measure geogrid and geotextile label widths using Konva Text refs when available
    useEffect(() => {
        if (geogridTextRef.current) {
            setGeogridTextWidth(geogridTextRef.current.getTextWidth?.() || 0);
            setGeogridTextHeight(geogridTextRef.current.getTextHeight?.() || 0);
        }
    }, [geogrid?.label]);
    useEffect(() => {
        if (geotextileTextRef.current) {
            setGeotextileTextWidth(geotextileTextRef.current.getTextWidth?.() || 0);
            setGeotextileTextHeight(geotextileTextRef.current.getTextHeight?.() || 0);
        }
    }, [geotextileLabel]);

    // posizine del label geogrid:
    const X_geogridLabelGroup = rectStartX + squareWidth / 2 - (geogridTextWidth + geogridLabelPadding) / 2;
    //const Y_geogridLabelGroup = geogridTextHeight + (geogridLabelPadding / 2);
    const Y_geogridLabelGroup = 9;
    // posizine del label geoTextTile:
    const X_geotextileLabelGroup = rectStartX + squareWidth / 2 - (geotextileTextWidth + geogridLabelPadding) / 2;
    //const Y_geogridLabelGroup = geogridTextHeight + (geogridLabelPadding / 2);
    const Y_geotextileLabelGroup = 9;

    // custom riga con pallini
    const nPoints = 6;
    const cerchioRaggio = 6;
    const circleSpacing = (rectEndX - (rectStartX + 0)) / (nPoints - 1);
    const circleXs = Array.from({ length: nPoints }, (_, i) => rectStartX - 6 + i * circleSpacing);
    // Log larghezze stage e contenitore principale

    if (alignFromBottom && targetSubgradeY !== undefined) {
        subgradeY = targetSubgradeY;
        baseY = subgradeY - baseCourseHeightPx;
        const topSpaceForImage = currentImageHeight + stageTopMargin;
        const layersEndY = subgradeY + subgradeHeightPx;
        stageHeight = customStageHeight ?? Math.max(layersEndY + stageBottomMargin, topSpaceForImage + totalLayerHeight + stageBottomMargin);
    } else {
        baseY = tireImageY + currentImageHeight;
        subgradeY = baseY + baseCourseHeightPx;
        stageHeight = customStageHeight ?? baseY + totalLayerHeight + stageBottomMargin;
    }
    // --- Aggiunta: considera anche il box saving se visibile --- allora aumenta l'altezza
    // Calcolo differenza di altezza tra unstabilized e stabilized pr farli uguali
    let savingBoxOffset = 0;
    if (showSavingBox && typeof unstabilizedStageHeight === 'number') {
        const stabilizedStageHeight = customStageHeight ?? baseY + totalLayerHeight + stageBottomMargin;
        const heightDiff = unstabilizedStageHeight - stabilizedStageHeight;
        // Usa solo la differenza, anche se negativa
        savingBoxOffset = heightDiff;
    }

    if (showSavingBox) {
        baseY = tireImageY + currentImageHeight + savingBoxOffset;
        subgradeY = baseY + baseCourseHeightPx;
        stageHeight = customStageHeight ?? baseY + totalLayerHeight + stageBottomMargin;
    }

    useEffect(() => {
        if (onStageHeightCalculated) onStageHeightCalculated(stageHeight);
        if (onSubgradeYCalculated) onSubgradeYCalculated(subgradeY);
    }, [stageHeight, subgradeY, onStageHeightCalculated, onSubgradeYCalculated]);

    // Dopo il calcolo di stageHeight

    return (
        <Stage width={forcedStageWidth ?? stageWidth} height={stageHeight}>
            <Layer>
                <Rect // questo mi mostra la dimensione dello stage
                    x={0}
                    y={0}
                    width={stageWidth}
                    height={stageHeight}
                    // fill='#fffec8'
                />
                {/* === Titoli Layer (2 strati) === */}
                {!hideLayerTitles && showLayers && (
                    <>
                        <Text
                            text={(t(baseCourse.name) ?? '').replace(/\s+/, '\n')} // vado a capo su due righe
                            // x={rectStartX - titleAreaWidth}
                            x={0}
                            y={baseY + baseCourseHeightPx / 2 + textOffsetY}
                            width={titleAreaWidth - 15}
                            align="right"
                            fontSize={12}
                            fill="black"
                        />
                        <Text text={t(subgrade.name) ?? ''} x={0} y={subgradeY + subgradeHeightPx / 2 + textOffsetY} width={titleAreaWidth - 15} align="right" fontSize={12} fill="black" />
                    </>
                )}
                {/* === Rettangoli Layer (2 strati) === */}
                {showLayers && (
                    <>
                        <Rect x={rectStartX} y={0} width={stageWidth} height={stageHeight} />
                        {/* Base Course con bordo superiore 3px e texture 1:1 repeat-y */}
                        <Rect
                            x={rectStartX}
                            y={baseY}
                            width={squareWidth}
                            height={baseCourseHeightPx}
                            fillPatternImage={texture2Img || undefined}
                            fillPatternRepeat="repeat-y"
                            fillPatternScale={{ x: 1, y: 1 }}
                            stroke={layerStrokeColor}
                            strokeWidth={layerStrokeWidth}
                            opacity={1}
                        />
                        {/* Bordo superiore 3px sopra il base course */}
                        <Line points={[rectStartX, baseY, rectEndX, baseY]} stroke="#e4cab7" strokeWidth={3} />
                        {/* Subgrade */}
                        <Rect
                            x={rectStartX}
                            y={subgradeY}
                            width={squareWidth}
                            height={subgradeHeightPx}
                            fillPatternImage={texture4Img || undefined}
                            fillPatternRepeat="repeat"
                            fillPatternScale={{ x: 0.5, y: 0.5 }}
                            stroke={layerStrokeColor}
                            strokeWidth={layerStrokeWidth}
                        />
                    </>
                )}
                {/* === Linea Geogriglia (Condizionale) verde === */}
                {showLayers && showGeogrid && geogrid && typeof geogrid.positionFromBottomOfBase === 'number' && (
                    <Group y={subgradeY} x={0}>
                        <Line
                            points={[rectStartX, 0, rectEndX, 0]}
                            stroke={geogridColor}
                            strokeWidth={3}
                            shadowColor="#FFFFFF"
                            // shadowBlur={8}
                            opacity={0.8}
                            lineCap="round"
                        />
                        {/* Cerchietti */}
                        {circleXs.map((x, idx) => (
                            <Circle
                                key={idx}
                                x={x + 6}
                                y={0}
                                radius={cerchioRaggio}
                                fill="white"
                                stroke={geogridColor}
                                strokeWidth={2}
                                shadowColor="#FFFFFF"
                                // shadowBlur={8}
                                opacity={1}
                            />
                        ))}
                        {geogrid.label && (
                            <Group x={X_geogridLabelGroup} y={Y_geogridLabelGroup}>
                                <Rect
                                    width={geogridTextWidth + geogridLabelPadding}
                                    height={geogridTextHeight + geogridLabelPadding / 2}
                                    fill="#FFFFFF"
                                    cornerRadius={6}
                                    x={0}
                                    y={-1}
                                    stroke="#828282"
                                    strokeWidth={1}
                                />
                                <Text ref={geogridTextRef} text={geogrid.label} fontSize={12} fontStyle="bold" fill={'black'} x={geogridLabelPadding / 2} y={5} />
                            </Group>
                        )}
                    </Group>
                )}
                {/* {subbaseGeogrid?.label && (
                    <Group x={X_geogridLabelGroup} y={Y_geogridLabelGroup}>
                        <Rect width={subbaseGeogridTextWidth + geogridLabelPadding} height={subbaseGeogridTextHeight + geogridLabelPadding/2} fill='#FFFFFF' cornerRadius={6} x={0} y={-1} stroke='#828282' strokeWidth={2} />
                        <Text ref={subbaseGeogridTextRef} text={subbaseGeogrid.label} fontSize={12} fontStyle='bold' fill={'black'} x={geogridLabelPadding / 2} y={5} />
                    </Group>
                )} */}
                {/* === Linea Geotextile  (Condizionale) Rossa === */}
                {showLayers && showGeotextile && (
                    <Group y={subgradeY} x={0}>
                        <Line
                            points={[rectStartX, 0, rectEndX, 0]}
                            stroke={geogridColor}
                            strokeWidth={3}
                            shadowColor="#FFFFFF"
                            // shadowBlur={8}
                            opacity={0.8}
                            lineCap="round"
                        />
                        {/* Cerchietti */}
                        {circleXs.map((x, idx) => (
                            <Circle
                                key={idx}
                                x={x + 6}
                                y={0}
                                radius={cerchioRaggio}
                                fill="white"
                                stroke={geogridColor}
                                strokeWidth={2}
                                shadowColor="#FFFFFF"
                                // shadowBlur={8}
                                opacity={1}
                            />
                        ))}
                        {geotextileLabel && (
                            <Group x={X_geotextileLabelGroup} y={Y_geotextileLabelGroup}>
                                <Rect
                                    width={geotextileTextWidth + geogridLabelPadding}
                                    height={geotextileTextHeight + geogridLabelPadding / 2}
                                    fill="#FFFFFF"
                                    cornerRadius={6}
                                    x={0}
                                    y={-1}
                                    stroke="#828282"
                                    strokeWidth={1}
                                />
                                <Text ref={geotextileTextRef} text={geotextileLabel} fontSize={12} fontStyle="bold" fill={'black'} x={geogridLabelPadding / 2} y={5} />
                            </Group>
                        )}
                    </Group>
                )}
                {/* === container/tire (Condizionale) === */}
                {showLayers && showTire && image && (
                    <KonvaImage image={image} x={rectStartX + squareWidth / 2 - currentImageWidth / 2} y={baseY - currentImageHeight} width={currentImageWidth} height={currentImageHeight} />
                )}
                {/* === Linee di Quota (Condizionali) === */}
                {showLeftDimension && typeof leftDimensionValue === 'number' && leftDimensionValue > 0 && (
                    <Group>
                        <Rect x={leftDimLineX + thickDimLineSpacing} y={baseY} width={thickDimLineWidth} height={baseCourseHeightPx} fill={'dimColor'} />
                        <Line points={[rectStartX - extensionLineOffset, baseY, rectStartX - extensionLineOffset - extensionLineLength, baseY]} stroke={dimColor} strokeWidth={2} dash={[3, 2]} />
                        <Line
                            points={[rectStartX - extensionLineOffset, subgradeY, rectStartX - extensionLineOffset - extensionLineLength, subgradeY]}
                            stroke={dimColor}
                            strokeWidth={2}
                            dash={[3, 2]}
                        />
                        <Arrow points={[leftDimLineX, baseY, leftDimLineX, subgradeY]} {...dimArrowProps} strokeWidth={dimStrokeWidth} pointerAtBeginning={true} />
                        <Text text={`${leftDimensionValue.toFixed(2)} ${unitLabel}`} x={leftDimAreaWidth - 5} y={baseY + baseCourseHeightPx / 2 + textOffsetY} {...dimTextProps} rotation={-90} />
                    </Group>
                )}
                {showRightDimension && typeof rightDimensionValue === 'number' && rightDimensionValue > 0 && (
                    <Group>
                        <Line points={[rectEndX + extensionLineOffset, baseY, rectEndX + extensionLineOffset + extensionLineLength, baseY]} stroke={marrone} strokeWidth={1} dash={[3, 2]} />
                        <Line points={[rectEndX + extensionLineOffset, subgradeY, rectEndX + extensionLineOffset + extensionLineLength, subgradeY]} stroke={marrone} strokeWidth={1} dash={[3, 2]} />
                        <Arrow points={[rightDimLineX, baseY, rightDimLineX, subgradeY]} {...dimArrowProps} pointerAtBeginning={true} />
                        <Text text={`${rightDimensionValue.toFixed(2)} ${unitLabel}`} x={rightDimLineX + 15} y={baseY + baseCourseHeightPx / 2 + textOffsetY} {...dimTextProps} />
                    </Group>
                )}
                {/* === Box Saving Percentage (Condizionale) === */}
                {showLayers && showSavingBox && typeof savingPercentage === 'number' && savingPercentage > 0 && (
                    <Group
                        // x={(forcedStageWidth ?? stageWidth) - (savingBoxWidth + 10)}
                        x={rectStartX + squareWidth / 2 + currentImageWidth}
                        y={savingBoxY + 20}
                        width={savingBoxWidth}>
                        <Rect width={savingBoxWidth} height={savingBoxHeight} fill="white" stroke="#004652" strokeWidth={3} cornerRadius={4} />
                        <Text text={t('thickness_saving') ?? 'Saving'} x={0} y={12} width={savingBoxWidth} align="center" fontSize={15} fill={dimColor} fontStyle="italic" />
                        <Text
                            text={`-${savingPercentage.toFixed(0)}%`}
                            width={savingBoxWidth}
                            height={savingBoxHeight}
                            align="center"
                            y={-8}
                            verticalAlign="bottom"
                            fontSize={30}
                            fill="#004652"
                            fontStyle="bold"
                        />
                    </Group>
                )}
            </Layer>
        </Stage>
    );
};
export default TireAndLayers;
